// Create namespace for public API
window.MapModule = window.MapModule || {};

(() => {
    // --- Configuration ---
    const config = {
        distributorsDataUrl: dataFiles.distributors,
        salesCompaniesDataUrl: dataFiles.salesOffices,
        distributorIcon: "/Content/img/icons/distributor.svg",
        salesCompanyIcon: "/Content/img/icons/salesoffice.svg",
        maxClusterZoomLevel: 5,
        worldPos: { center: [7, 43], zoom: 2 },
        mapStyle: "grayscale_light",
        mapLanguage: "en-US",
        subscriptionKey: "8fzA87LI83C4xLDaMMFsrSu7U6gdX9sGbdD8VnKqrVs"
    };

    // --- DOM Elements ---
    const dom = {
        countrySelect: document.getElementById('countrySelect'),
        salesCompanyInfo: document.getElementById('salesCompanyInfoElement'),
        noDistributorsInfo: document.getElementById('noDistributorsInfoElement'),
        myLocationBtn: document.getElementById('myLocationBtn'),
        locationList: document.getElementById('locationList'),
        spinnerLoader: document.getElementById('spinnerLoader'),
        distributorsHeading: document.getElementById('distributorsHeading'),
        hideAsideBtn: document.querySelector('.hide-aside')
    };

    // --- State ---
    let map, popup, allDistributorShapes, distributorsDatasource, salesCompaniesDatasource;
    let distributorLayer, salesCompaniesLayer, clusterBubbleLayer, clusterLabelLayer, centerMarker;
    let moveendHandler = null, moveendHandlerActive = false;
    let userPosition = null;

    // --- Utility Functions ---
    const debounce = (fn, delay) => {
        let timer = null;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    };

    const getSelectedCountry = () =>
        dom.countrySelect.options[dom.countrySelect.selectedIndex]?.value;

    const getQueryStringParam = (param) =>
        new URLSearchParams(window.location.search).get(param);

    const getAddressLine = (properties) => [
        properties["AddressLine"] && ` ${properties["AddressLine"]}`,
        properties["City"],
        properties["Municipality"] && `, ${properties["Municipality"]}`,
        properties["AdminDivision"] && `, ${properties["AdminDivision"]}`,
        properties["PostCode"] && ` ${properties["PostCode"]}`,
        properties["Country"] && ` ${properties["Country"]}`,
    ].filter(Boolean).join(",");

    const getProductareas = (properties) => [
        properties["Pneumatics"] && `<span class="badge pn" title="${mapTranslations.productareas.pn}"></span>`,
        properties["Hydraulics"] && `<span class="badge hy" title="${mapTranslations.productareas.hy}"></span>`,
        properties["Hp"] && `<span class="badge hp" title="${mapTranslations.productareas.hp}"></span>`,
        properties["Fluids"] && `<span class="badge fl" title="${mapTranslations.productareas.fl}"></span>`,
        properties["BreathingAir"] && `<span class="badge ba" title="${mapTranslations.productareas.ba}"></span>`,
        properties["MultiAuto"] && `<span class="badge ma" title="${mapTranslations.productareas.ma}"></span>`,
        properties["ThermalControl"] && `<span class="badge tc" title="${mapTranslations.productareas.tc}"></span>`,
        properties["Hr"] && `<span class="badge hr" title="${mapTranslations.productareas.hr}"></span>`,
    ].filter(Boolean).join(" ");

    // --- Map Initialization ---
    function initializeMap() {
        map = new atlas.Map("mapRenderElement", {
            center: config.worldPos.center,
            zoom: config.worldPos.zoom,
            style: config.mapStyle,
            view: "Auto",
            language: config.mapLanguage,
            authOptions: {
                authType: "subscriptionKey",
                subscriptionKey: config.subscriptionKey,
            },
            renderWorldCopies: false,
            showFeedbackLink: false,
            scrollZoomInteraction: false,
            dragRotateInteraction: false,
        });

        map.events.add("ready", onMapReady);
    }

    // --- Map Ready Handler ---
    function onMapReady() {
        popup = new atlas.Popup({ pixelOffset: [0, -18], closeButton: true });

        window.addEventListener('keydown', onCtrlKeyDown);
        window.addEventListener('keyup', onCtrlKeyUp);

        map.controls.add(new atlas.control.ZoomControl(), { position: "top-right" });

        map.imageSprite.add("distributorIcon", config.distributorIcon);
        map.imageSprite.add("salesCompanyIcon", config.salesCompanyIcon);

        centerMarker = new atlas.HtmlMarker({
            htmlContent: '<div class="map-center-icon" title="Your location" aria-label="Your location"></div>',
            position: map.getCamera().center,
            visible: false,
        });
        map.markers.add(centerMarker);

        // Data sources
        distributorsDatasource = new atlas.source.DataSource(null, {
            cluster: true,
            clusterMaxZoom: config.maxClusterZoomLevel - 1,
            clusterRadius: 50,
        });
        salesCompaniesDatasource = new atlas.source.DataSource(null, { cluster: false });
        map.sources.add(distributorsDatasource);
        map.sources.add(salesCompaniesDatasource);

        // Layers
        clusterBubbleLayer = new atlas.layer.BubbleLayer(distributorsDatasource, "clusterBubbleLayerId", {
            radius: ["interpolate", ["linear"], ["get", "point_count"], 0, 20, 150, 40],
            interactive: true,
            iconOptions: { image: "none", ignorePlacement: true, allowOverlap: true },
            color: "rgba(52, 138, 27, 0.7)",
            strokeColor: "rgba(154, 217, 103, 0.7)",
            strokeWidth: 2,
            filter: ["has", "point_count"],
        });
        clusterLabelLayer = new atlas.layer.SymbolLayer(distributorsDatasource, null, {
            iconOptions: { image: "none", ignorePlacement: true, allowOverlap: true },
            textOptions: {
                textField: ["get", "point_count_abbreviated"],
                size: 12,
                font: ["StandardFont-Bold"],
                offset: [0, 0.4],
                color: "white",
            },
        });
        distributorLayer = new atlas.layer.SymbolLayer(distributorsDatasource, "distributorLayerId", {
            iconOptions: {
                image: "distributorIcon",
                font: ["SegoeUi-Bold"],
                anchor: "center",
                ignorePlacement: true,
                allowOverlap: true,
            },
            filter: ["!", ["has", "point_count"]],
        });
        salesCompaniesLayer = new atlas.layer.SymbolLayer(salesCompaniesDatasource, null, {
            iconOptions: {
                image: "salesCompanyIcon",
                font: ["SegoeUi-Bold"],
                anchor: "center",
                ignorePlacement: true,
                allowOverlap: true,
            },
            filter: ["!", ["has", "point_count"]],
        });
        map.layers.add([clusterBubbleLayer, clusterLabelLayer, distributorLayer, salesCompaniesLayer]);

        // Map event listeners
        const layers = [clusterBubbleLayer, distributorLayer, salesCompaniesLayer];
        map.events.add("mouseover", layers, () => map.getCanvasContainer().style.cursor = "pointer");
        map.events.add("mouseout", layers, () => map.getCanvasContainer().style.cursor = "grab");
        map.events.add("click", clusterBubbleLayer, onClusterClick);
        map.events.add("click", distributorLayer, onDistributorClick);
        map.events.add("click", salesCompaniesLayer, onSalesCompanyClick);

        // moveend handler
        moveendHandler = onMoveEnd;
        map.events.add("moveend", moveendHandler);
        moveendHandlerActive = true;

        // UI event listeners
        dom.myLocationBtn.addEventListener("click", onMyLocationClick);
        dom.countrySelect.addEventListener("change", onCountryChange);
        dom.hideAsideBtn.addEventListener("click", onHideAsideClick);

        // AWAIT data loading - Sales companies and distributors must be loaded before we can list locations
        loadAllData().then((success) => {
            if (success) {
                map.events.addOnce('idle', onMapIdle);
            } else {
                console.error('Map data loading failed');
            }
        });
    }

    // --- Event Handlers ---
    function onCtrlKeyDown(e) {
        if (e.ctrlKey) map.setUserInteraction({ scrollZoomInteraction: true });
    }
    function onCtrlKeyUp(e) {
        if (!e.ctrlKey) map.setUserInteraction({ scrollZoomInteraction: false });
    }
    function onClusterClick(e) {
        map.setCamera({ center: e.position, zoom: map.getCamera().zoom + 2 });
    }
    function onDistributorClick(e) {
        if (e.shapes?.length > 0) {
            showDistributorPopup(e.position, e.shapes[0].getProperties());
            e.preventDefault();
        }
    }
    function onSalesCompanyClick(e) {
        if (e.shapes?.length > 0) {
            showSalesCompanyPopup(e.position, e.shapes[0].getProperties());
            e.preventDefault();
        }
    }
    function onMyLocationClick(event) {
        event.preventDefault();
        setMapToUserLocation();
    }
    function onCountryChange(event) {
        const countryName = event.target.value;
        filterByCountry(countryName);
    }
    function onHideAsideClick() {
        const aside = dom.hideAsideBtn.closest("aside");
        aside.classList.toggle("hidden");
        dom.hideAsideBtn.classList.toggle("hidden");
    }
    function onMapIdle() {
        const countryFromQuery = getQueryStringParam('country');
        if (countryFromQuery) {      
            // Find the actual option value (original case)
            const matchedOption = Array.from(dom.countrySelect.options)
                .find(opt => opt.value.toLowerCase() === countryFromQuery.toLowerCase());
            if (matchedOption) {
                dom.countrySelect.value = matchedOption.value;
                filterByCountry(matchedOption.value);
            }
            
        } else {
            filterByCountry(false);
            //listLocations();
        }
    }

    let pendingRenderHandler = null;
    let pendingTimeout = null;
    let renderHandlerActive = false;

    function onMoveEnd() {
        // Clean up any previous handlers/timeouts
        if (pendingRenderHandler) {
            map.events.remove("render", pendingRenderHandler);
            pendingRenderHandler = null;
        }
        if (pendingTimeout) {
            clearTimeout(pendingTimeout);
            pendingTimeout = null;
        }
        renderHandlerActive = false;

        showLocationListLoader();

        // Attach a one-time render handler if not already attached
        pendingRenderHandler = () => {
            if (renderHandlerActive) return;
            renderHandlerActive = true;

            // Remove the handler immediately to ensure it only fires once
            map.events.remove("render", pendingRenderHandler);
            pendingRenderHandler = null;

            // Wait a bit to ensure clusters/layers are updated
            pendingTimeout = setTimeout(() => {
                listLocations(); // Only fires once per moveend
                pendingTimeout = null;
                renderHandlerActive = false;
            }, 400); // Adjust as needed
        };
        map.events.add("render", pendingRenderHandler);
    }


    // --- Data Loading ---
    async function loadDistributorData(dataSource, dataUrl) {
        try {
            const response = await fetch(dataUrl);
            const data = await response.json();
            const features = data.map(obj => {
                const lat = parseFloat(obj.Lat) ?? NaN;
                const lng = parseFloat(obj.Lng) ?? NaN;
                if (isNaN(lat) || isNaN(lng)) return null;
                return new atlas.data.Feature(
                    new atlas.data.Point([lng, lat]),
                    {
                        Name: obj.Name,
                        AddressLine: obj.Adress,
                        City: obj.City,
                        Municipality: '',
                        AdminDivision: '',
                        Country: obj.Country,
                        PostCode: obj.ZipCode,
                        Phone: (obj.Phone && obj.Phone.length > 5) ? obj.Phone : undefined,
                        Website: obj.Website,
                        Pneumatics: obj.ProductArea_Pneumatics,
                        Hydraulics: obj.ProductArea_MediumHydraulics,
                        Hp: obj.ProductArea_HP,
                        Fluids: obj.ProductArea_Fluids,
                        BreathingAir: obj.ProductArea_BreathingAir,
                        MultiAuto: obj.ProductArea_MultiAuto,
                        ThermalControl: obj.ProductArea_ThermalControl,
                        Hr: obj.ProductArea_HR
                    }
                );
            }).filter(Boolean);
            dataSource.add(features);
            allDistributorShapes = distributorsDatasource.getShapes();
            return { success: true, count: features.length };
        } catch (error) {
            console.error('Error loading distributor data:', error);
        }
        return true;
    }

    async function loadSalesCompaniesData(dataSource, dataUrl) {
        try {
            const response = await fetch(dataUrl);
            const data = await response.json();
            const features = data.map(obj => {
                const lat = parseFloat(obj.Latitude) ?? NaN;
                const lng = parseFloat(obj.Longitude) ?? NaN;
                if (isNaN(lat) || isNaN(lng)) return null;
                return new atlas.data.Feature(
                    new atlas.data.Point([lng, lat]),
                    {
                        Id: obj.Id,
                        Name: obj.Name,
                        AddressLine: obj.Address,
                        City: obj.City,
                        Country: obj.Country,
                        PostCode: obj.ZipCode,
                        Email: obj.Email,
                        SalesOfficeLink: salesOfficesPageUrls?.[obj.Id?.toString()] ?? undefined,
                        Phone: obj.Phone,
                        Markets: obj.Markets.replace(",", ", ")
                    }
                );
            }).filter(Boolean);
            dataSource.add(features);
            return { success: true, count: features.length };
        } catch (error) {
            console.error('Error loading sales companies data:', error);
        }
        return true;
    }

    async function loadAllData() {
        try {
            console.log('Starting map data loading...');
        
            // Load both datasets and wait for completion
            const [distributorResult, salesResult] = await Promise.all([
                loadDistributorData(distributorsDatasource, config.distributorsDataUrl),
                loadSalesCompaniesData(salesCompaniesDatasource, config.salesCompaniesDataUrl)
            ]);
        
            // Check if both loads succeeded
            if (!distributorResult.success) {
                console.log(`Distributor loading failed: ${distributorResult.error}`);
                return false;
            }
        
            if (!salesResult.success) {
                console.log(`Sales company loading failed: ${salesResult.error}`);
                return false;
            }
        
            console.log(`Map data loaded successfully! Distributors: ${distributorResult.count}, Sales Offices: ${salesResult.count}`);
        
            return true;
        } catch (error) {
            console.error("Error in loading sequence:", error);
            return false;
        }
    }

    // --- Location Listing & Filtering ---
    async function listLocations(shapes) {
        showLocationListLoader();
        if (shapes) {
            // If shapes are provided, use them directly
            updateLocationsList(shapes);
        } else {
            // Otherwise, get visible locations based on the current map view
            try {
                const locations = await getVisibleLocations();
                updateLocationsList(locations);
            } catch (error) {
                console.error('Error listing visible locations:', error);
            }
        }
        // If a moveend handler is set, ensure it is active
        if (moveendHandler && !moveendHandlerActive) {
            map.events.add("moveend", moveendHandler);
            moveendHandlerActive = true;
        }
    }

    async function getVisibleLocations() {
        const shapes = [
            ...map.layers.getRenderedShapes(map.getCamera().bounds, distributorLayer),
            ...map.layers.getRenderedShapes(map.getCamera().bounds, clusterBubbleLayer),
        ];
        const visibleLocations = [];
        for (const shape of shapes) {
            if (shape.properties?.cluster) {
                try {
                    const leaves = await distributorsDatasource.getClusterLeaves(
                        shape.properties.cluster_id,
                        Infinity,
                        0
                    );
                    visibleLocations.push(...leaves);
                } catch (error) {
                    console.error("Error getting cluster leaves:", error);
                }
            } else {
                visibleLocations.push(shape);
            }
        }
        return visibleLocations;
    }

    function filterByCountry(countryName) {
        if (moveendHandler && moveendHandlerActive) {
            map.events.remove("moveend", moveendHandler);
            moveendHandlerActive = false;
        }
        dom.distributorsHeading.innerHTML = "";
        dom.locationList.innerHTML = "";
        dom.salesCompanyInfo.innerHTML = "";
        dom.noDistributorsInfo.innerHTML = "";   

        if (countryName) {
            //console.log(`Filtering distributors by country: ${countryName}`);
            let salesCompanyShape = getSalesCompany(countryName, "");
            showSalesCompany(salesCompanyShape);
            distributorsDatasource.setShapes(allDistributorShapes);
            const filteredShapes = distributorsDatasource
                .getShapes()
                .filter(shape => shape.getProperties().Country.toUpperCase() === countryName.toUpperCase());
            distributorsDatasource.setShapes(filteredShapes);

            if (filteredShapes.length > 0) {
                //console.log(`Found ${filteredShapes.length} distributors for country: ${countryName}`);
                let bounds = filteredShapes.reduce((acc, shape) => {
                    const shapeBounds = atlas.data.BoundingBox.fromData(shape);
                    return acc ? atlas.data.BoundingBox.merge(acc, shapeBounds) : shapeBounds;
                }, null);
                if (bounds) {
                    const [minLng, minLat, maxLng, maxLat] = bounds;
                    const minSpan = 0.05;
                    let spanLng = Math.abs(maxLng - minLng);
                    let spanLat = Math.abs(maxLat - minLat);
                    if (spanLng < minSpan && spanLat < minSpan) {
                        const centerLng = (minLng + maxLng) / 2;
                        const centerLat = (minLat + maxLat) / 2;
                        bounds = [
                            centerLng - minSpan / 2, centerLat - minSpan / 2,
                            centerLng + minSpan / 2, centerLat + minSpan / 2
                        ];
                    }
                    map.setCamera({ bounds, padding: 100 });
                } else {
                    map.setCamera(config.worldPos);
                }
                dom.distributorsHeading.innerHTML = `${mapTranslations.distributorsIn} ${countryName}`;
                listLocations(filteredShapes);
            } else {
                //console.log(`No distributors found for country: ${countryName}`);
                let properties = salesCompanyShape.getProperties();
                
                dom.noDistributorsInfo.innerHTML = `${mapTranslations.noDistributorsIn} ${countryName} contact ${properties["Name"]}`;
                map.setCamera(config.worldPos);
            }

        } else {
            console.log("No country selected, showing all distributors.");
            if (moveendHandler && moveendHandlerActive) {
                map.events.remove("moveend", moveendHandler);
                moveendHandlerActive = false;
            }
            popup.close();
            distributorsDatasource.setShapes(allDistributorShapes);
            map.setCamera(config.worldPos);
            dom.distributorsHeading.innerHTML = `${mapTranslations.distributors}`;
            listLocations(allDistributorShapes);
        }


    }

    // --- UI Rendering ---
    function updateLocationsList(locations) {
        // Todo update distributorsHeading.innerHTML from here instead of any other place
        console.log(`Updating locations list with ${locations.length} locations.`);

        dom.locationList.innerHTML = "";

        if (locations.length > 0) {
            let distances = {};
            if (userPosition) {
                locations.forEach(shape => {
                    if (shape instanceof atlas.Shape) {
                        distances[shape.getId()] = Math.round(
                            atlas.math.getDistanceTo(userPosition, shape.getCoordinates(), "kilometers") * 100
                        ) / 100;
                    }
                });
                locations.sort((x, y) => distances[x.getId()] - distances[y.getId()]);
            }
            const html = locations.map(location => {
                const properties = location.getProperties();
                const distance = userPosition ? `${distances[location.getId()]} kilometers away` : "";
                return renderDistributorListItem(location.getId(), properties, distance);
            }).join("");
            dom.locationList.innerHTML = html;
            dom.locationList.addEventListener("click", onLocationListClick);
        }
        dom.locationList.scrollTop = 0;
        dom.spinnerLoader.classList.add("d-none");
    }

    function renderDistributorListItem(id, properties, distance) {
        return `
            <li>
                <p class="item" data-id="${id}">
                    <span class="title">${properties["Name"] || ''}</span>
                    <span class="address">${getAddressLine(properties)}</span>
                    <span>${distance}</span>
                </p>
                ${properties["Phone"] ? `<a href="tel:${properties["Phone"]}"><img src="/Content/img/icons/phone.svg"/> ${properties["Phone"]}</a>` : ''}
                ${properties["Website"] ? `<a href="${properties["Website"]}" target="_blank"><img src="/Content/img/icons/ext-link.svg"/> ${properties["Website"]}</a>` : ''}
                <p>${getProductareas(properties)}</p>
            </li>
        `;
    }

    function showLocationListLoader() {
        dom.spinnerLoader.classList.remove("d-none");
    }

    function onLocationListClick(event) {
        const item = event.target.closest("p.item");
        if (item) {
            itemSelected(item.getAttribute("data-id"));
        }
    }

    function itemSelected(id) {
        const shape = distributorsDatasource.getShapeById(id);
        const properties = shape.getProperties();
        const position = shape.getCoordinates();
        const offset = map.getCanvas().width < 700 ? [0, -80] : undefined;
        map.setCamera({ center: position, centerOffset: offset });
        showDistributorPopup(position, properties);
    }

    function showDistributorPopup(position, properties) {
        popup.close();
        let popupContainer = popup.getPopupContainer();
        popupContainer.classList.add("popupDistributor");
        //popup.getPopupContainer().addClass("my-custom-class");
        console.log(popup.getPopupContainer());
        const html = `
            <div class="storePopup">
                <div class="popupTitle"><span>${properties["Name"]}</span></div>
                <div class="popupContent">
                    <span class="address">${getAddressLine(properties)}</span>
                    ${properties["Phone"] ? `<a href="tel:${properties["Phone"]}"><img src="/Content/img/icons/phone.svg"/> ${properties["Phone"]}</a>` : ''}
                    ${properties["Website"] ? `<a href="${properties["Website"]}" target="_blank"><img src="/Content/img/icons/ext-link.svg"/> ${properties["Website"]}</a>` : ''}
                    <p>${getProductareas(properties)}</p>
                </div>
            </div>
        `;
        popup.setOptions({ visible: true, content: html, position: position });
        popup.open(map);
    }

    function showSalesCompany(salesCompanyShape) {
        if (!salesCompanyShape) {
            dom.salesCompanyInfo.innerHTML = "";
            return;
        }
        const properties = salesCompanyShape.getProperties();
        const position = salesCompanyShape.getCoordinates();
        dom.salesCompanyInfo.innerHTML = renderSalesCompanyInfo(properties);
        let companyItem = dom.salesCompanyInfo.querySelector(".companyItem");
        companyItem.addEventListener("click", () => {
            showSalesCompanyPopup(position, properties);
        });
        showSalesCompanyPopup(position, properties);
    }

    function renderSalesCompanyInfo(properties) {
        return `
            <p class="companyItem" data-id="${properties["Id"]}">
                <span class="title">${properties["Name"]}</span>
                <span class="address">${getAddressLine(properties)}</span>
            </p>
            ${properties["Email"] ? `<a href="mailto:${properties["Email"]}"><img src="/Content/img/icons/email-square.svg"/> ${properties["Email"]}</a>` : ''}
            ${properties["Phone"] ? `<a href="tel:${properties["Phone"]}"><img src="/Content/img/icons/phone.svg"/> ${properties["Phone"]}</a>` : ''}
            ${properties["SalesOfficeLink"] ? `<a href="${properties["SalesOfficeLink"].Url}"><img src="/Content/img/icons/int-link.svg"/> ${properties["SalesOfficeLink"].PageName}</a>` : ''}
        `;
    }

    function showSalesCompanyPopup(position, properties) {
        popup.close();
        let popupContainer = popup.getPopupContainer();
        popupContainer.classList.remove("popupDistributor");
        const html = `
            <div class="storePopup">
                <div class="popupTitle"><span>${properties["Name"]}</span></div>
                <div class="popupContent">
                    <span class="address">${getAddressLine(properties)}</span>
                    ${properties["Email"] ? `<a href="mailto:${properties["Email"]}"><img src="/Content/img/icons/email-square.svg"/> ${properties["Email"]}</a>` : ''}
                    ${properties["Phone"] ? `<a href="tel:${properties["Phone"]}"><img src="/Content/img/icons/phone.svg"/> ${properties["Phone"]}</a>` : ''}
                    ${properties["SalesOfficeLink"] ? `<a href="${properties["SalesOfficeLink"].Url}"><img src="/Content/img/icons/int-link.svg"/> ${properties["SalesOfficeLink"].PageName}</a>` : ''}
                </div>
            </div>
        `;
        popup.setOptions({ visible: true, content: html, position: position });
        popup.open(map);
    }

    function getSalesCompany(countryName, salesCompanyName) {
        const shapes = salesCompaniesDatasource.getShapes();
        return shapes.find(shape => {
            const props = shape.getProperties();
            if (countryName) {
                const marketsArr = props.Markets.split("|").map(market => market.toLowerCase());
                return marketsArr.includes(countryName.toLowerCase());
            }
            if (salesCompanyName) {
                return props.Name.toLowerCase() === salesCompanyName.toLowerCase();
            }
            return false;
        });
    }

    function setMapToUserLocation() {
        dom.myLocationBtn.classList.add("active");
        if (!navigator.geolocation || !navigator.permissions) {
            alert('Geolocation is not supported by this browser.');
            dom.myLocationBtn.classList.remove("active");
            return;
        }
        navigator.permissions.query({ name: "geolocation" }).then((permissionStatus) => {
            if (permissionStatus.state === "denied") {
                alert("You have denied location access. Please enable it in your browser settings.");
                dom.myLocationBtn.classList.remove("active");
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (moveendHandler && moveendHandlerActive) {
                        map.events.remove("moveend", moveendHandler);
                        moveendHandlerActive = false;
                    }
                    const pos = [position.coords.longitude, position.coords.latitude];
                    userPosition = pos;
                    if (centerMarker) {
                        centerMarker.setOptions({ position: pos, visible: true });
                    }
                    dom.countrySelect.value = "";
                    showSalesCompany(false);
                    popup.close();
                    distributorsDatasource.setShapes(allDistributorShapes);
                    map.setCamera({ center: pos, zoom: config.maxClusterZoomLevel + 3 });
                    dom.distributorsHeading.innerHTML = `${mapTranslations.distributors}`;
                    listLocations(allDistributorShapes);

                },
                (error) => handleLocationError(error),
                { enableHighAccuracy: false, timeout: 20000, maximumAge: 0 }
            );
            dom.myLocationBtn.classList.remove("active");
        }).catch((error) => {
            console.warn("Error checking geolocation permissions:", error);
            dom.myLocationBtn.classList.remove("active");
        });
    }

    function handleLocationError(error) {
        switch (error.code) {
            case error.PERMISSION_DENIED:
                alert("User denied the request for Geolocation.");
                break;
            case error.POSITION_UNAVAILABLE:
                alert("Location information is unavailable.");
                break;
            case error.TIMEOUT:
                alert("The request to get user location timed out.");
                break;
            case error.UNKNOWN_ERROR:
                alert("An unknown error occurred.");
                break;
        }
    }

    // --- Export public init function ---
    window.MapModule.init = function() {
        initializeMap();
    };
})();
